import { ofType } from 'redux-observable';
import { forkJoin, from, interval, merge, of } from 'rxjs';
import { bufferTime, catchError, debounceTime, filter, mergeMap, startWith, switchMap } from 'rxjs/operators';

import { CustomerTransactionType, TradingType, TransferStatus, WalletType } from 'common/api/autogenerated/client-area';

import { i18n } from 'common/hooks/useI18n/useI18n.hook';
import { notify } from 'common/hooks/useNotification';
import svgIcons from 'common/svgIcons';
import { CommonEpic } from 'common/types';

import earnActionTypes from 'views/earn/redux/earn.actionTypes';

import { socketActionTypes } from 'blocks/SocketProvider/redux';
import { CustomerBalanceUpdatedDto } from 'blocks/SocketProvider/types';

import {
  actionErrorGetBalance,
  actionErrorGetCurrencies,
  actionErrorGetTransactions,
  actionErrorGetWalletConfiguration,
  actionErrorGetWallets,
  actionGetPaymentCardError,
  actionGetPaymentCardSuccess,
  actionGetTransactions,
  actionGetWallets,
  actionLoadingGetTransactions,
  actionLoadingGetWallets,
  actionSuccessGetBalance,
  actionSuccessGetCurrencies,
  actionSuccessGetWalletConfiguration,
  actionUpdateWalletBalanceSuccess,
} from './actions';
import walletActionTypes from './wallet.actionTypes';
import * as clientAreaApiClient from './wallet.api';
import { WalletTransactionsFiltersType } from './wallet.initialState';

export const epicGetWallets: CommonEpic<typeof clientAreaApiClient> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(walletActionTypes.LOADING_GET_WALLETS),
    switchMap(() =>
      merge(interval(5000).pipe(startWith(0))).pipe(
        switchMap(() =>
          forkJoin({
            wallets: from(api.getWallets()),
            balance: from(api.getBalance()),
          }).pipe(
            mergeMap(({ wallets, balance }) => {
              const filteredWallets = wallets.filter((wallet) => {
                if (localStorage.getItem('show-tests-wallets') || window.location.host === 'www.xbo.com') return true;
                return (
                  !wallet.currency?.startsWith('TST') &&
                  !wallet.currency?.startsWith('TEST') &&
                  !wallet.currency?.startsWith('PERF')
                );
              });

              return of(actionGetWallets(filteredWallets), actionSuccessGetBalance(balance));
            }),
            catchError((error) => of(actionErrorGetWallets(error))),
          ),
        ),
      ),
    ),
  );

export const epicGetBalance: CommonEpic<typeof clientAreaApiClient> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(walletActionTypes.LOADING_GET_BALANCE),
    debounceTime(1000),
    mergeMap(() =>
      from(api.getBalance()).pipe(
        mergeMap((response) => of(actionSuccessGetBalance(response))),
        catchError((error) => of(actionErrorGetBalance(error))),
      ),
    ),
  );

export const epicGetCurrencies: CommonEpic<typeof clientAreaApiClient> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(walletActionTypes.LOADING_GET_CURRENCIES),
    debounceTime(1000),
    mergeMap((action) =>
      from(api.getCurrencies(action.payload)).pipe(
        mergeMap((response) => of(actionSuccessGetCurrencies(response))),
        catchError((error) => of(actionErrorGetCurrencies(error))),
      ),
    ),
  );

export const epicGetWalletConfiguration: CommonEpic<typeof clientAreaApiClient> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(walletActionTypes.LOADING_GET_WALLET_CONFIGURATION),
    debounceTime(1000),
    mergeMap(() =>
      from(api.getWalletConfiguration()).pipe(
        mergeMap((response) => of(actionSuccessGetWalletConfiguration(response))),
        catchError((error) => of(actionErrorGetWalletConfiguration(error))),
      ),
    ),
  );

export const epicGetTransactions: CommonEpic<typeof clientAreaApiClient> = (action$, state$, { api }) =>
  action$.pipe(
    ofType(
      walletActionTypes.LOADING_GET_TRANSACTIONS,
      walletActionTypes.UPDATE_TRANSACTIONS_FILTERS,
      walletActionTypes.MASS_PAYMENTS_CREATED,
      walletActionTypes.MASS_PAYMENTS_COPMLETED,
    ),
    debounceTime(1000),
    mergeMap(() => {
      const {
        value: {
          walletReducer: {
            transactions: { filters },
          },
        },
      } = state$;

      const _filters: WalletTransactionsFiltersType = JSON.parse(JSON.stringify(filters));

      // Include CustomerTransactionType.WITHDRAWAL_REQUEST
      // when WalletType.FIAT & CustomerTransactionType.WITHDRAWAL is selected
      if (
        (!_filters?.currencyType || _filters?.currencyType === WalletType.FIAT) &&
        _filters?.types?.includes(CustomerTransactionType.WITHDRAWAL)
      )
        _filters?.types.push(CustomerTransactionType.WITHDRAWAL_REQUEST);

      // Include CustomerTransactionType.EARN_REWARD
      // when TradingType.SPOT & CustomerTransactionType.BONUS is selected
      if (
        (!_filters?.accountTradingType || _filters?.accountTradingType === TradingType.SPOT) &&
        _filters?.types?.includes(CustomerTransactionType.BONUS)
      )
        _filters?.types.push(CustomerTransactionType.EARN_REWARD);

      // Include CustomerTransactionType.EARN_TOP_UP
      // when !_filters?.accountTradingType & CustomerTransactionType.TRANSFER is selected
      if (!_filters?.accountTradingType && _filters?.types?.includes(CustomerTransactionType.TRANSFER))
        _filters?.types.push(CustomerTransactionType.EARN_TOP_UP);

      return from(
        api.getTransactions(
          filters?.currencyType,
          filters?.currency || filters?.search,
          filters?.dateFrom,
          filters?.dateTo,
          filters?.accountTradingType,
          _filters?.types,
          filters?.status,
        ),
      ).pipe(
        mergeMap((response) => of(actionGetTransactions(response ?? []))),
        catchError(() => of(actionErrorGetTransactions())),
      );
    }),
  );

export const epicGetBalanceUpdate: CommonEpic<typeof clientAreaApiClient> = (action$) =>
  action$.pipe(
    ofType(socketActionTypes.SIGNALR_WALLET_BALANCE_UPDATED),
    bufferTime(1000),
    filter((stream) => !!stream.length),
    switchMap((actionsCollection) => {
      const updates = actionsCollection.reduce(
        (balances: CustomerBalanceUpdatedDto[], action) => [...balances, action.payload],
        [],
      );
      return of(actionUpdateWalletBalanceSuccess(updates));
    }),
  );

export const epicEarnCreatedEvent: CommonEpic<typeof clientAreaApiClient> = (action$) =>
  action$.pipe(
    ofType(earnActionTypes.SIGNALR_CREATE_EARN),
    filter(({ payload }) => payload.status === 'Completed'),
    mergeMap(() => of(actionLoadingGetWallets())),
  );

export const transferUpdatedNotification: CommonEpic<typeof clientAreaApiClient> = (action$) =>
  action$.pipe(
    ofType(socketActionTypes.SIGNALR_TRANSFER_UPDATED),
    mergeMap(({ payload }) => {
      const isFailed = payload.status === TransferStatus.FAILED || payload.status === TransferStatus.DECLINED;

      const notificationPayload: Parameters<typeof notify>[0] = {
        toastId: `transfer-created-notification-${payload.transferId}`,
        autoClose: 5000,
        isLoading: false,
        icon: isFailed ? svgIcons.Error() : undefined,
        titleFn: (translate: i18n['translateToText']) =>
          translate(isFailed ? 'mobile:transfer:failed:title' : 'mobile:transfer:success:title')
            .replace('{source}', translate(`mobile:wallets:page:${payload.sourceAccountType.toLowerCase()}`))
            .replace('{destination}', translate(`mobile:wallets:page:${payload.destinationAccountType.toLowerCase()}`))
            .replace('{amount}', String(payload.amount))
            .replace('{currency}', payload.currency),

        description: undefined,
      };

      if (isFailed)
        notificationPayload.descriptionFn = (translate: i18n['translateToText']) =>
          translate('mobile:transfer:failed:description');

      notify(notificationPayload);

      return of(actionLoadingGetWallets(), actionLoadingGetTransactions());
    }),
  );

export const epicGetPaymentCard: CommonEpic<typeof clientAreaApiClient> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(walletActionTypes.LOADING_GET_PAYMENT_CARD),
    mergeMap(() =>
      from(api.getPaymentCard()).pipe(
        mergeMap((response) => of(actionGetPaymentCardSuccess(response))),
        catchError((error) => of(actionGetPaymentCardError(error))),
      ),
    ),
  );
